import { useQuery } from '@tanstack/react-query';
import { fetchAnalyticsSummary, fetchDeals, fetchTasks, type Task } from '../api/resources';

function formatTaskDue(task: Task) {
  if (!task.due_date) return '—';
  const options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };
  if (task.due_time !== null) {
    options.timeStyle = 'short';
  }
  return new Date(task.due_date * 1000).toLocaleString([], options);
}

export function DashboardPage() {
  const { data: summary } = useQuery({ queryKey: ['analytics-summary'], queryFn: fetchAnalyticsSummary });
  const { data: deals } = useQuery({ queryKey: ['deals'], queryFn: fetchDeals });
  const { data: tasks } = useQuery({ queryKey: ['tasks'], queryFn: fetchTasks });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Welcome back</h1>
          <p>Track pipeline health, follow-ups, and revenue all in one place.</p>
        </div>
      </div>

      <div className="card-grid">
        <div className="card">
          <h3>Leads</h3>
          <p className="stat">{summary?.leads ?? '—'}</p>
        </div>
        <div className="card">
          <h3>Deals Won</h3>
          <p className="stat">{summary?.dealsWon ?? '—'}</p>
        </div>
        <div className="card">
          <h3>Revenue</h3>
          <p className="stat">${summary?.revenue?.toLocaleString() ?? '—'}</p>
        </div>
        <div className="card">
          <h3>Tasks Completed</h3>
          <p className="stat">{summary?.tasksCompleted ?? '—'}</p>
        </div>
      </div>

      <div className="card">
        <h3>Pipeline Highlights</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Deal</th>
              <th>Stage</th>
              <th>Value</th>
              <th>Contact</th>
            </tr>
          </thead>
          <tbody>
            {deals?.slice(0, 5).map((deal) => (
              <tr key={deal.id}>
                <td>{deal.name}</td>
                <td>{deal.stage_name}</td>
                <td>${deal.value.toLocaleString()}</td>
                <td>
                  {[deal.first_name, deal.last_name].filter(Boolean).join(' ')}
                </td>
              </tr>
            ))}
            {!deals?.length && (
              <tr>
                <td colSpan={4}>No deals yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Upcoming Tasks</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Status</th>
              <th>Due</th>
              <th>Owner</th>
            </tr>
          </thead>
          <tbody>
            {tasks?.slice(0, 5).map((task) => (
              <tr key={task.id}>
                <td>{task.title}</td>
                <td>{task.status}</td>
                <td>{formatTaskDue(task)}</td>
                <td>{task.assignee_email ?? 'Unassigned'}</td>
              </tr>
            ))}
            {!tasks?.length && (
              <tr>
                <td colSpan={4}>No tasks created yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
